import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { caseFileAPI, archiveRequestAPI, documentAPI } from '../api/api';
import CaseFileForm from '../components/CaseFiles/CaseFileForm';
import ColumnControl from '../components/Common/ColumnControl';
import CaseFileDetail from './CaseFileDetail';
import './CaseFiles.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { CASE_FILE_STATUS_LABELS, DAVA_TURU_LABELS, davaTuruEtiketi, getSortedDavaTuruEntries } from '../utils/constants';
import { toISODateOrNull, formatDateForDisplay } from '../utils/dateUtils';
import { normalizeForSearch } from '../utils/searchUtils';
import { onayla } from '../utils/onayla';

/** Sekme basligindaki dava turu. "Diger" ise elle yazilan ad gosterilir. */
const sekmeTuru = (tab) => {
  const etiket = davaTuruEtiketi({ DavaTuru: tab.davaTuru, DavaTuruDiger: tab.davaTuruDiger });
  return etiket && etiket !== 'Seçiniz' ? etiket : '';
};

const CaseFiles = ({ ozelMode = false }) => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const tabsStorageKey = ozelMode ? 'ozelCaseFiles_openTabs' : 'caseFiles_openTabs';
  const activeTabStorageKey = ozelMode ? 'ozelCaseFiles_activeTab' : 'caseFiles_activeTab';
  const [caseFiles, setCaseFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    davaTuru: ''
  });
  const [columnFilters, setColumnFilters] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Sayfa içi sekmeler: 'all' (Tüm Dosyalar) veya açık dosya sekmeleri (localStorage'da kalıcı)
  const [openTabs, setOpenTabs] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(tabsStorageKey) || 'null');
      if (Array.isArray(saved)) {
        return saved
          .filter((t) => t && t.id != null)
          .map((t) => ({
            id: String(t.id),
            foyNo: t.foyNo ?? '',
            muvekkil: t.muvekkil ?? 'Dosya',
            davaTuru: t.davaTuru ?? null,
            davaTuruDiger: t.davaTuruDiger ?? null
          }));
      }
    } catch (_) {}
    return [];
  });
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(activeTabStorageKey);
      if (saved) return saved;
    } catch (_) {}
    return 'all';
  });
  const [draggingTabId, setDraggingTabId] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(tabsStorageKey, JSON.stringify(openTabs));
    } catch (_) {}
  }, [openTabs, tabsStorageKey]);

  useEffect(() => {
    if (!caseFiles.length) return;
    setOpenTabs((prev) => {
      let changed = false;
      const next = prev.map((t) => {
        // "Diğer" turunde elle yazilan ad da gerekli; o yuzden ikisini birlikte tamamliyoruz.
        if (t.davaTuru != null && t.davaTuru !== '' && t.davaTuruDiger !== undefined) return t;
        const file = caseFiles.find((f) => String(f.Id ?? f.id) === String(t.id));
        if (!file) return t;
        changed = true;
        return {
          ...t,
          davaTuru: file.DavaTuru ?? file.davaTuru ?? null,
          davaTuruDiger: file.DavaTuruDiger ?? file.davaTuruDiger ?? null
        };
      });
      return changed ? next : prev;
    });
  }, [caseFiles]);

  useEffect(() => {
    try {
      localStorage.setItem(activeTabStorageKey, activeTab);
    } catch (_) {}
  }, [activeTab, activeTabStorageKey]);

  // Kayıtlı aktif sekme artık yoksa Tüm Dosyalar'a dön
  useEffect(() => {
    if (activeTab !== 'all' && !openTabs.some((t) => t.id === activeTab)) {
      setActiveTab('all');
    }
  }, [activeTab, openTabs]);

  const openFileTab = (file) => {
    const fileId = file.Id ?? file.id;
    if (fileId == null) return;
    const idStr = String(fileId);
    setOpenTabs(prev => {
      if (prev.some(t => t.id === idStr)) return prev;
      return [
        ...prev,
        {
          id: idStr,
          foyNo: file.FoyNo ?? file.foyNo ?? '',
          muvekkil: file.Muvekkil ?? file.muvekkil ?? 'Dosya',
          davaTuru: file.DavaTuru ?? file.davaTuru ?? null,
          davaTuruDiger: file.DavaTuruDiger ?? file.davaTuruDiger ?? null
        }
      ];
    });
    setActiveTab(idStr);
  };

  const closeFileTab = (idStr, e) => {
    if (e) e.stopPropagation();
    setOpenTabs(prev => {
      const remaining = prev.filter(t => t.id !== idStr);
      setActiveTab(current => {
        if (current !== idStr) return current;
        // Kapatılan sekme aktifse: son kalan sekmeye ya da Tüm Dosyalar'a geç
        return remaining.length > 0 ? remaining[remaining.length - 1].id : 'all';
      });
      return remaining;
    });
  };

  const handleTabDragStart = (e, idStr) => {
    setDraggingTabId(idStr);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idStr);
  };

  const handleTabDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleTabDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain') || draggingTabId;
    setDraggingTabId(null);
    if (!sourceId || sourceId === targetId) return;
    setOpenTabs((prev) => {
      const next = [...prev];
      const from = next.findIndex((t) => t.id === sourceId);
      const to = next.findIndex((t) => t.id === targetId);
      if (from < 0 || to < 0) return prev;
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const handleTabDragEnd = () => setDraggingTabId(null);

  // Column visibility control
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('caseFiles_visibleColumns');
    if (saved) {
      try {
        const merged = { ...JSON.parse(saved), davaTuru: true };
        localStorage.setItem('caseFiles_visibleColumns', JSON.stringify(merged));
        return merged;
      } catch {
        return {};
      }
    }
    // Default: all columns visible
    return {
      foyNo: true,
      muvekkil: true,
      davaTuru: true,
      muvekkilTC: true,
      muvekkilAracSigortaSirketi: true,
      sorumlu: true,
      karsiSigorta: true,
      karsiTaraf1: true,
      karsiTaraf2: true,
      muvekkilPlaka: true,
      karsiTarafPlaka: true,
      kazaSehri: true,
      kazaTarihi: true,
      dosyaGelisTarihi: true,
      tahkimBasvuruTarihi: true,
      referans: true,
      raporTarihi: true,
      zamanAsimiTarihi: true,
      durusmaTarihi: true,
      status: true,
      karsiTarafVekili: true,
      muvekkilTelefon: true,
      muvekkilIBAN: true,
      sigortaBasvuruTarihi: false,
      davaAcilisTarihi: false,
      sozlesmeOrani: false,
      mahkemeEsas: false,
      icraDosyaNo: true,
      savcilikCezaDosyaNo: true,
      tahkimBasvuruNo: true,
      itirazBasvuruNo: true,
      mahkemaBilgisi: true,
      sulhDurumu: true,
      ibraAsamasi: true,
      gelenOdeme: true,
      gidenOdeme: true,
      smm: true,
      sonIslemYapan: true,
      createdDate: true,
      updatedDate: true
    };
  });

  const caseFileColumns = [
    { key: 'foyNo', label: 'Foy No' },
    { key: 'muvekkil', label: 'Müvekkil' },
    { key: 'davaTuru', label: 'Dava Türü' },
    { key: 'muvekkilTC', label: 'TC' },
    { key: 'muvekkilAracSigortaSirketi', label: 'Müvekkil Araç Sigorta Şirketi' },
    { key: 'sorumlu', label: 'Sorumlu' },
    { key: 'karsiSigorta', label: 'Karşı Sigorta' },
    { key: 'karsiTaraf1', label: 'Karşı Taraf 1' },
    { key: 'karsiTaraf2', label: 'Karşı Taraf 2' },
    { key: 'muvekkilPlaka', label: 'Müvekkil Plaka' },
    { key: 'karsiTarafPlaka', label: 'Karşı Taraf Plaka' },
    { key: 'kazaSehri', label: 'Kaza Şehri' },
    { key: 'kazaTarihi', label: 'Kaza Tarihi' },
    { key: 'dosyaGelisTarihi', label: 'Dosya Geliş Tarihi' },
    { key: 'tahkimBasvuruTarihi', label: 'Tahkim Başvuru Tarihi' },
    { key: 'referans', label: 'Referans' },
    { key: 'raporTarihi', label: 'Rapor Tarihi' },
    { key: 'zamanAsimiTarihi', label: 'Zaman Aşımı Tarihi' },
    { key: 'durusmaTarihi', label: 'Duruşma Tarihi ve Saati' },
    { key: 'status', label: 'Durum' },
    { key: 'karsiTarafVekili', label: 'Karşı Taraf Vekili' },
    { key: 'muvekkilTelefon', label: 'Müvekkil Telefon' },
    { key: 'muvekkilIBAN', label: 'Müvekkil IBAN' },
    { key: 'sigortaBasvuruTarihi', label: 'Sigorta Başvuru Tarihi' },
    { key: 'davaAcilisTarihi', label: 'Dava Açılış Tarihi' },
    { key: 'sozlesmeOrani', label: 'Sözleşme Oranı' },
    { key: 'mahkemeEsas', label: 'Mahkeme Esas' },
    { key: 'icraDosyaNo', label: 'İcra Dosya No' },
    { key: 'savcilikCezaDosyaNo', label: 'Savcılık/Ceza Dosya No' },
    { key: 'tahkimBasvuruNo', label: 'Tahkim Başvuru No' },
    { key: 'itirazBasvuruNo', label: 'İtiraz Başvuru No' },
    { key: 'mahkemaBilgisi', label: 'Mahkeme Bilgisi' },
    { key: 'sulhDurumu', label: 'Sulh Durumu' },
    { key: 'ibraAsamasi', label: 'İbra Aşaması' },
    { key: 'gelenOdeme', label: 'Gelen Ödeme' },
    { key: 'gidenOdeme', label: 'Giden Ödeme' },
    { key: 'smm', label: 'SMM' },
    { key: 'sonIslemYapan', label: 'Son İşlem Yapan' },
    { key: 'createdDate', label: 'Oluşturma Tarihi' },
    { key: 'updatedDate', label: 'Güncelleme Tarihi' }
  ];

  const COLUMN_ORDER_KEY = 'caseFiles_columnOrder';

  const mergeColumnOrder = (saved, defaults) => {
    const known = new Set(defaults);
    const ordered = (Array.isArray(saved) ? saved : [])
      .map((k) => (k === 'dosyaTuru' ? 'davaTuru' : k))
      .filter((k) => known.has(k));
    defaults.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
    const pinned = ordered.filter((k) => k !== 'davaTuru');
    const muvekkilIndex = pinned.indexOf('muvekkil');
    if (muvekkilIndex >= 0) pinned.splice(muvekkilIndex + 1, 0, 'davaTuru');
    else pinned.splice(1, 0, 'davaTuru');
    return pinned;
  };

  const persistColumnOrder = (order) => {
    try {
      localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(order));
      // Yedek anahtar (eski temizleyicilere karşı)
      localStorage.setItem('caseFiles_columnOrder_v2', JSON.stringify(order));
    } catch (_) {}
  };

  const readSavedColumnOrder = () => {
    try {
      const primary = JSON.parse(localStorage.getItem(COLUMN_ORDER_KEY) || 'null');
      if (Array.isArray(primary) && primary.length) return primary;
      const backup = JSON.parse(localStorage.getItem('caseFiles_columnOrder_v2') || 'null');
      if (Array.isArray(backup) && backup.length) return backup;
    } catch (_) {}
    return null;
  };

  const [columnOrder, setColumnOrder] = useState(() => {
    const defaults = caseFileColumns.map((c) => c.key);
    return mergeColumnOrder(readSavedColumnOrder(), defaults);
  });
  const [draggingColumnKey, setDraggingColumnKey] = useState(null);
  const columnDragMovedRef = useRef(false);
  const draggingColumnKeyRef = useRef(null);
  const lastDragTargetRef = useRef(null);

  // Kolon sırasını her değişiklikte kalıcı kaydet (hemen + effect)
  useEffect(() => {
    persistColumnOrder(columnOrder);
  }, [columnOrder]);

  const orderedVisibleColumns = useMemo(() => {
    const byKey = Object.fromEntries(caseFileColumns.map((c) => [c.key, c]));
    return columnOrder.map((k) => byKey[k]).filter((c) => c && visibleColumns[c.key] === true);
  }, [columnOrder, visibleColumns]);

  // Sütun kontrolü listesi de aynı sırayı kullansın
  const orderedAllColumns = useMemo(() => {
    const byKey = Object.fromEntries(caseFileColumns.map((c) => [c.key, c]));
    return columnOrder.map((k) => byKey[k]).filter(Boolean);
  }, [columnOrder]);

  const moveColumn = (sourceKey, targetKey) => {
    if (!sourceKey || !targetKey || sourceKey === targetKey) return;
    setColumnOrder((prev) => {
      const next = [...prev];
      const from = next.indexOf(sourceKey);
      const to = next.indexOf(targetKey);
      if (from < 0 || to < 0 || from === to) return prev;
      next.splice(from, 1);
      next.splice(to, 0, sourceKey);
      persistColumnOrder(next);
      return next;
    });
  };

  const handleColumnDragStart = (e, key) => {
    columnDragMovedRef.current = false;
    draggingColumnKeyRef.current = key;
    lastDragTargetRef.current = null;
    setDraggingColumnKey(key);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', key);
      e.dataTransfer.setData('application/x-col-key', key);
    } catch (_) {}
  };

  const handleColumnDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Sürüklerken üzerine gelinen kolonla yer değiştir (drop kaçsa bile sıra güncellenir)
  const handleColumnDragEnter = (e, targetKey) => {
    e.preventDefault();
    const sourceKey = draggingColumnKeyRef.current;
    if (!sourceKey || sourceKey === targetKey) return;
    if (lastDragTargetRef.current === targetKey) return;
    lastDragTargetRef.current = targetKey;
    columnDragMovedRef.current = true;
    moveColumn(sourceKey, targetKey);
  };

  const handleColumnDrop = (e, targetKey) => {
    e.preventDefault();
    e.stopPropagation();
    columnDragMovedRef.current = true;
    let sourceKey = draggingColumnKeyRef.current;
    try {
      sourceKey =
        e.dataTransfer.getData('application/x-col-key') ||
        e.dataTransfer.getData('text/plain') ||
        sourceKey;
    } catch (_) {}
    moveColumn(sourceKey, targetKey);
    draggingColumnKeyRef.current = null;
    setDraggingColumnKey(null);
  };

  const handleColumnDragEnd = () => {
    draggingColumnKeyRef.current = null;
    setDraggingColumnKey(null);
    // Sürükleme sonrası click=sort tetiklenmesin (kısa süre)
    window.setTimeout(() => { columnDragMovedRef.current = false; }, 50);
  };

  const renderCaseFileCell = (file, key, status, davaTuru) => {
    switch (key) {
      case 'foyNo': return <td key={key} className="col-foy-no">{file.FoyNo ?? file.foyNo ?? 'N/A'}</td>;
      case 'muvekkil': return <td key={key} className="col-muvekkil">{file.Muvekkil ?? file.muvekkil ?? 'N/A'}</td>;
      case 'muvekkilTC': return <td key={key}>{file.MuvekkilTC ?? file.muvekkilTC ?? '-'}</td>;
      case 'davaTuru': return <td key={key} className="col-dava-turu">{davaTuruEtiketi(file) || 'Seçiniz'}</td>;
      case 'muvekkilAracSigortaSirketi': return <td key={key}>{file.MuvekkilAracSigortaSirketi ?? file.muvekkilAracSigortaSirketi ?? '-'}</td>;
      case 'sorumlu': return (
        <td key={key} style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.SorumluAdi ?? file.sorumluAdi ?? ''}>
          {file.SorumluAdi ?? file.sorumluAdi ?? '-'}
        </td>
      );
      case 'karsiSigorta': return <td key={key}>{file.KarsiTarafSigortaSirketi ?? file.karsiTarafSigortaSirketi ?? file.KarsiSigorta ?? file.karsiSigorta ?? '-'}</td>;
      case 'karsiTaraf1': return <td key={key}>{file.KarsiTaraf1 ?? file.karsiTaraf1 ?? '-'}</td>;
      case 'karsiTaraf2': return <td key={key}>{file.KarsiTaraf2 ?? file.karsiTaraf2 ?? '-'}</td>;
      case 'muvekkilPlaka': return <td key={key}>{file.MuvekkilPlaka ?? file.muvekkilPlaka ?? '-'}</td>;
      case 'karsiTarafPlaka': return <td key={key}>{file.KarsiTarafPlaka ?? file.karsiTarafPlaka ?? '-'}</td>;
      case 'kazaSehri': return <td key={key}>{file.KazaSehri ?? file.kazaSehri ?? '-'}</td>;
      case 'kazaTarihi': return <td key={key}>{formatDate(file.KazaTarihi ?? file.kazaTarihi)}</td>;
      case 'dosyaGelisTarihi': return <td key={key}>{formatDate(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi)}</td>;
      case 'tahkimBasvuruTarihi': return <td key={key}>{formatDate(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi)}</td>;
      case 'referans': return <td key={key}>{file.ReferansAdi ?? file.referansAdi ?? file.Referans ?? file.referans ?? '-'}</td>;
      case 'raporTarihi': return <td key={key}>{formatDate(file.RaporTarihi ?? file.raporTarihi)}</td>;
      case 'zamanAsimiTarihi': return <td key={key}>{formatDate(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi)}</td>;
      case 'durusmaTarihi': {
        const dt = file.DurusmaTarihi ?? file.durusmaTarihi;
        return <td key={key}>{dt ? formatDateForDisplay(dt) : '-'}</td>;
      }
      case 'status': return (
        <td key={key} style={{ minWidth: '150px', width: '150px' }}>
          <span className={`status-badge status-${status}`}>
            {CASE_FILE_STATUS_LABELS[status] ?? 'Bilinmiyor'}
          </span>
        </td>
      );
      case 'muvekkilTelefon': return <td key={key}>{file.MuvekkilTelefon ?? file.muvekkilTelefon ?? '-'}</td>;
      case 'muvekkilIBAN': return <td key={key}>{file.MuvekkilIBAN ?? file.muvekkilIBAN ?? '-'}</td>;
      case 'sigortaBasvuruTarihi': return <td key={key}>{formatDate(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi)}</td>;
      case 'davaAcilisTarihi': return <td key={key}>{formatDate(file.DavaAcilisTarihi ?? file.davaAcilisTarihi)}</td>;
      case 'sozlesmeOrani': return <td key={key}>{file.SozlesmeOrani ?? file.sozlesmeOrani ?? '-'}</td>;
      case 'mahkemeEsas': return <td key={key}>{file.MahkemeEsas ?? file.mahkemeEsas ?? '-'}</td>;
      case 'icraDosyaNo': return <td key={key}>{file.IcraDosyaNo ?? file.icraDosyaNo ?? '-'}</td>;
      case 'savcilikCezaDosyaNo': return <td key={key}>{file.SavcilikCezaDosyaNo ?? file.savcilikCezaDosyaNo ?? '-'}</td>;
      case 'tahkimBasvuruNo': return <td key={key}>{file.TahkimBasvuruNo ?? file.tahkimBasvuruNo ?? '-'}</td>;
      case 'itirazBasvuruNo': return <td key={key}>{file.ItirazBasvuruNo ?? file.itirazBasvuruNo ?? '-'}</td>;
      case 'mahkemaBilgisi': return (
        <td key={key} style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? ''}>
          {file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? '-'}
        </td>
      );
      case 'sulhDurumu': return <td key={key}>{file.SulhDurumu ?? file.sulhDurumu ?? '-'}</td>;
      case 'ibraAsamasi': return <td key={key}>{file.IbraAsamasi ?? file.ibraAsamasi ?? '-'}</td>;
      case 'gelenOdeme': return <td key={key}>{file.GelenOdemeDurumu ?? file.gelenOdemeDurumu ?? '-'}</td>;
      case 'gidenOdeme': return <td key={key}>{file.GidenOdemeDurumu ?? file.gidenOdemeDurumu ?? '-'}</td>;
      case 'smm': return <td key={key}>{file.SMM ?? file.smm ?? '-'}</td>;
      case 'karsiTarafVekili': return <td key={key}>{file.KarsiTarafSigortaSirketiVekili ?? file.karsiTarafSigortaSirketiVekili ?? '-'}</td>;
      case 'sonIslemYapan': return <td key={key}>{file.SonIslemYapanAdi ?? file.sonIslemYapanAdi ?? '-'}</td>;
      case 'createdDate': return <td key={key}>{formatDate(file.CreatedDate ?? file.createdDate)}</td>;
      case 'updatedDate': return <td key={key}>{formatDate(file.UpdatedDate ?? file.updatedDate)}</td>;
      default: return null;
    }
  };

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      const newState = { ...prev, [columnKey]: !prev[columnKey] };
      localStorage.setItem('caseFiles_visibleColumns', JSON.stringify(newState));
      return newState;
    });
  };

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  useEffect(() => {
    // Token kontrolü - localStorage'dan da kontrol et
    const localToken = localStorage.getItem('token');
    if (token || localToken) {
      loadCaseFiles();
    } else {
      // Token yoksa login sayfasına yönlendir
      navigate('/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    applyFilters();
  }, [caseFiles, filters, columnFilters]);

  const loadCaseFiles = async () => {
    setLoading(true);
    try {
      const data = await caseFileAPI.getAll(!!ozelMode);
      // FoyNo'ya göre büyükten küçüğe sırala
      const sortedData = [...data].sort((a, b) => {
        const foyNoA = a.FoyNo || a.foyNo || 0;
        const foyNoB = b.FoyNo || b.foyNo || 0;
        return foyNoB - foyNoA; // Büyükten küçüğe
      });
      setCaseFiles(sortedData);
      setFilteredFiles(sortedData);
    } catch (error) {
      console.error('Error loading case files:', error);
      // 401 hatası durumunda interceptor login'e yönlendirmeyecek (path kontrolü var)
      // Sadece hata mesajı göster
      if (error.response?.status === 401) {
        // Sessizce geç, interceptor zaten login'e yönlendirmeyecek
        return;
      }
      alert('Dosyalar yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...caseFiles];

    if (filters.search) {
      const searchLower = normalizeForSearch(filters.search);
      filtered = filtered.filter(file => {
        // Tüm alanlarda arama yap - Helper fonksiyon (Türkçe ı/i uyumu)
        const getValue = (obj, key) => {
          const value = obj[key] ?? obj[key.charAt(0).toLowerCase() + key.slice(1)];
          if (value === null || value === undefined) return '';
          return normalizeForSearch(value);
        };
        
        // Tarih formatlama helper
        const formatDateForSearch = (dateValue) => {
          if (!dateValue) return '';
          try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleDateString('tr-TR', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }).toLocaleLowerCase('tr-TR');
          } catch {
            return '';
          }
        };
        
        // Enum label'larını al
        const davaTuruLabel = file.DavaTuru !== null && file.DavaTuru !== undefined 
          ? normalizeForSearch(davaTuruEtiketi(file)) 
          : '';
        const statusLabel = file.Status !== null && file.Status !== undefined 
          ? normalizeForSearch(CASE_FILE_STATUS_LABELS[file.Status] ?? CASE_FILE_STATUS_LABELS[file.status] ?? '') 
          : '';
        
        // Tüm kolonları kontrol et
        const searchFields = [
          getValue(file, 'FoyNo'),
          getValue(file, 'Muvekkil'),
          getValue(file, 'MuvekkilTC'),
          getValue(file, 'MuvekkilAracSigortaSirketi'),
          getValue(file, 'SorumluAdi'),
          getValue(file, 'KarsiTarafSigortaSirketi'),
          getValue(file, 'KarsiTaraf1'),
          getValue(file, 'KarsiTaraf2'),
          getValue(file, 'MuvekkilTelefon'),
          getValue(file, 'MuvekkilIBAN'),
          getValue(file, 'MuvekkilPlaka'),
          getValue(file, 'KarsiTarafPlaka'),
          getValue(file, 'KazaSehri'),
          normalizeForSearch(formatDateForSearch(file.KazaTarihi ?? file.kazaTarihi)),
          normalizeForSearch(formatDateForSearch(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi)),
          davaTuruLabel,
          normalizeForSearch(formatDateForSearch(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi)),
          getValue(file, 'ReferansAdi'),
          normalizeForSearch(formatDateForSearch(file.RaporTarihi ?? file.raporTarihi)),
          normalizeForSearch(formatDateForSearch(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi)),
          normalizeForSearch(formatDateForSearch(file.DurusmaTarihi ?? file.durusmaTarihi)),
          statusLabel,
          normalizeForSearch(formatDateForSearch(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi)),
          normalizeForSearch(formatDateForSearch(file.DavaAcilisTarihi ?? file.davaAcilisTarihi)),
          getValue(file, 'SozlesmeOrani'),
          getValue(file, 'MahkemeEsas'),
          getValue(file, 'IcraDosyaNo'),
          getValue(file, 'SavcilikCezaDosyaNo'),
          getValue(file, 'TahkimBasvuruNo'),
          getValue(file, 'ItirazBasvuruNo'),
          getValue(file, 'MahkemaBilgisi'),
          getValue(file, 'SulhDurumu'),
          getValue(file, 'IbraAsamasi'),
          getValue(file, 'GelenOdemeDurumu'),
          getValue(file, 'GidenOdemeDurumu'),
          getValue(file, 'SMM'),
          getValue(file, 'KarsiTarafSigortaSirketiVekili'),
          getValue(file, 'SonIslemYapanAdi'),
          normalizeForSearch(formatDateForSearch(file.CreatedDate ?? file.createdDate)),
          normalizeForSearch(formatDateForSearch(file.UpdatedDate ?? file.updatedDate))
        ];
        
        // Tüm alanlarda arama yap
        return searchFields.some(field => field.includes(searchLower));
      });
    }

    if (filters.status) {
      filtered = filtered.filter(file => {
        const status = file.Status ?? file.status;
        return String(status) === filters.status;
      });
    }

    if (filters.davaTuru) {
      filtered = filtered.filter(file => {
        const davaTuru = file.DavaTuru ?? file.davaTuru;
        return String(davaTuru) === filters.davaTuru;
      });
    }

    // Kolon bazlı filtreler
    Object.keys(columnFilters).forEach(columnKey => {
      const filterValue = columnFilters[columnKey];
      if (!filterValue || !filterValue.trim()) return;
      
      const searchLower = normalizeForSearch(filterValue);
      
      filtered = filtered.filter(file => {
        const getValue = (obj, key) => {
          const value = obj[key] ?? obj[key.charAt(0).toLowerCase() + key.slice(1)];
          if (value === null || value === undefined) return '';
          return normalizeForSearch(value);
        };

        const formatDateForSearch = (dateValue) => {
          if (!dateValue) return '';
          try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleDateString('tr-TR');
          } catch {
            return '';
          }
        };

        switch(columnKey) {
          case 'foyNo':
            return getValue(file, 'FoyNo').includes(searchLower);
          case 'muvekkil':
            return getValue(file, 'Muvekkil').includes(searchLower);
          case 'muvekkilTC':
            return getValue(file, 'MuvekkilTC').includes(searchLower);
          case 'dosyaTuru':
            return normalizeForSearch(davaTuruEtiketi(file) ||  '').includes(searchLower);
          case 'muvekkilAracSigortaSirketi':
            return getValue(file, 'MuvekkilAracSigortaSirketi').includes(searchLower);
          case 'sorumlu':
            return getValue(file, 'SorumluAdi').includes(searchLower);
          case 'karsiSigorta':
            return getValue(file, 'KarsiTarafSigortaSirketi').includes(searchLower);
          case 'karsiTaraf1':
            return getValue(file, 'KarsiTaraf1').includes(searchLower);
          case 'karsiTaraf2':
            return getValue(file, 'KarsiTaraf2').includes(searchLower);
          case 'muvekkilPlaka':
            return getValue(file, 'MuvekkilPlaka').includes(searchLower);
          case 'karsiTarafPlaka':
            return getValue(file, 'KarsiTarafPlaka').includes(searchLower);
          case 'kazaSehri':
            return getValue(file, 'KazaSehri').includes(searchLower);
          case 'kazaTarihi':
            return normalizeForSearch(formatDateForSearch(file.KazaTarihi ?? file.kazaTarihi)).includes(searchLower);
          case 'dosyaGelisTarihi':
            return normalizeForSearch(formatDateForSearch(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi)).includes(searchLower);
          case 'davaTuru':
            return normalizeForSearch(davaTuruEtiketi(file) ||  '').includes(searchLower);
          case 'tahkimBasvuruTarihi':
            return normalizeForSearch(formatDateForSearch(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi)).includes(searchLower);
          case 'referans':
            return getValue(file, 'ReferansAdi').includes(searchLower);
          case 'raporTarihi':
            return normalizeForSearch(formatDateForSearch(file.RaporTarihi ?? file.raporTarihi)).includes(searchLower);
          case 'zamanAsimiTarihi':
            return normalizeForSearch(formatDateForSearch(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi)).includes(searchLower);
          case 'durusmaTarihi':
            return normalizeForSearch(formatDateForSearch(file.DurusmaTarihi ?? file.durusmaTarihi)).includes(searchLower);
          case 'status':
            return normalizeForSearch(CASE_FILE_STATUS_LABELS[file.Status ?? file.status] ?? '').includes(searchLower);
          case 'karsiTarafVekili':
            return getValue(file, 'KarsiTarafSigortaSirketiVekili').includes(searchLower);
          case 'muvekkilTelefon':
            return getValue(file, 'MuvekkilTelefon').includes(searchLower);
          case 'muvekkilIBAN':
            return getValue(file, 'MuvekkilIBAN').includes(searchLower);
          case 'sigortaBasvuruTarihi':
            return normalizeForSearch(formatDateForSearch(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi)).includes(searchLower);
          case 'davaAcilisTarihi':
            return normalizeForSearch(formatDateForSearch(file.DavaAcilisTarihi ?? file.davaAcilisTarihi)).includes(searchLower);
          case 'sozlesmeOrani':
            return getValue(file, 'SozlesmeOrani').includes(searchLower);
          case 'mahkemeEsas':
            return getValue(file, 'MahkemeEsas').includes(searchLower);
          case 'icraDosyaNo':
            return getValue(file, 'IcraDosyaNo').includes(searchLower);
          case 'savcilikCezaDosyaNo':
            return getValue(file, 'SavcilikCezaDosyaNo').includes(searchLower);
          case 'tahkimBasvuruNo':
            return getValue(file, 'TahkimBasvuruNo').includes(searchLower);
          case 'itirazBasvuruNo':
            return getValue(file, 'ItirazBasvuruNo').includes(searchLower);
          case 'mahkemaBilgisi':
            return getValue(file, 'MahkemaBilgisi').includes(searchLower);
          case 'sulhDurumu':
            return getValue(file, 'SulhDurumu').includes(searchLower);
          case 'ibraAsamasi':
            return getValue(file, 'IbraAsamasi').includes(searchLower);
          case 'gelenOdeme':
            return getValue(file, 'GelenOdemeDurumu').includes(searchLower);
          case 'gidenOdeme':
            return getValue(file, 'GidenOdemeDurumu').includes(searchLower);
          case 'smm':
            return getValue(file, 'SMM').includes(searchLower);
          case 'sonIslemYapan':
            return getValue(file, 'SonIslemYapanAdi').includes(searchLower);
          case 'createdDate':
            return normalizeForSearch(formatDateForSearch(file.CreatedDate ?? file.createdDate)).includes(searchLower);
          case 'updatedDate':
            return normalizeForSearch(formatDateForSearch(file.UpdatedDate ?? file.updatedDate)).includes(searchLower);
          default:
            return true;
        }
      });
    });

    setFilteredFiles(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Sıralama fonksiyonu
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleColumnHeaderClick = (columnKey) => {
    if (columnDragMovedRef.current) {
      columnDragMovedRef.current = false;
      return;
    }
    handleSort(columnKey);
  };

  // Sıralama okunu gösteren helper fonksiyon
  const SortIndicator = ({ column }) => (
    <span style={{ marginLeft: '5px', fontSize: '10px' }}>
      {sortColumn === column ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  // Sıralanmış dosyalar
  const sortedFilteredFiles = useMemo(() => {
    if (!sortColumn) return filteredFiles;
    const sorted = [...filteredFiles];
    sorted.sort((a, b) => {
      let aValue, bValue;
      const getValue = (obj, key) => {
        const value = obj[key] ?? obj[key.charAt(0).toLowerCase() + key.slice(1)];
        if (value === null || value === undefined) return '';
        return String(value);
      };
      
      switch (sortColumn) {
        case 'foyNo':
          aValue = parseInt(getValue(a, 'FoyNo')) || 0;
          bValue = parseInt(getValue(b, 'FoyNo')) || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'muvekkil':
          aValue = getValue(a, 'Muvekkil').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'Muvekkil').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'muvekkilTC':
          aValue = getValue(a, 'MuvekkilTC').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'MuvekkilTC').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'dosyaTuru':
          aValue = parseInt(getValue(a, 'DavaTuru')) || 0;
          bValue = parseInt(getValue(b, 'DavaTuru')) || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'sorumlu':
          aValue = getValue(a, 'SorumluAdi').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'SorumluAdi').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'karsiSigorta':
          aValue = getValue(a, 'KarsiTarafSigortaSirketi').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'KarsiTarafSigortaSirketi').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'karsiTaraf1':
          aValue = getValue(a, 'KarsiTaraf1').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'KarsiTaraf1').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'karsiTaraf2':
          aValue = getValue(a, 'KarsiTaraf2').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'KarsiTaraf2').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'muvekkilTelefon':
          aValue = getValue(a, 'MuvekkilTelefon').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'MuvekkilTelefon').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'muvekkilIBAN':
          aValue = getValue(a, 'MuvekkilIBAN').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'MuvekkilIBAN').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'sigortaBasvuruTarihi':
          aValue = a.SigortaBasvuruTarihi ?? a.sigortaBasvuruTarihi ? new Date(a.SigortaBasvuruTarihi ?? a.sigortaBasvuruTarihi).getTime() : 0;
          bValue = b.SigortaBasvuruTarihi ?? b.sigortaBasvuruTarihi ? new Date(b.SigortaBasvuruTarihi ?? b.sigortaBasvuruTarihi).getTime() : 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'davaAcilisTarihi':
          aValue = a.DavaAcilisTarihi ?? a.davaAcilisTarihi ? new Date(a.DavaAcilisTarihi ?? a.davaAcilisTarihi).getTime() : 0;
          bValue = b.DavaAcilisTarihi ?? b.davaAcilisTarihi ? new Date(b.DavaAcilisTarihi ?? b.davaAcilisTarihi).getTime() : 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'sozlesmeOrani':
          aValue = parseFloat(a.SozlesmeOrani ?? a.sozlesmeOrani) || 0;
          bValue = parseFloat(b.SozlesmeOrani ?? b.sozlesmeOrani) || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'mahkemeEsas':
          aValue = getValue(a, 'MahkemeEsas').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'MahkemeEsas').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'davaTuru':
          aValue = parseInt(getValue(a, 'DavaTuru')) || 0;
          bValue = parseInt(getValue(b, 'DavaTuru')) || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'status':
          aValue = parseInt(getValue(a, 'Status')) || 0;
          bValue = parseInt(getValue(b, 'Status')) || 0;
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'referans':
          aValue = getValue(a, 'ReferansAdi').toLocaleLowerCase('tr-TR');
          bValue = getValue(b, 'ReferansAdi').toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
        case 'dosyaGelisTarihi':
          aValue = a.DosyaGelisTarihi ?? a.dosyaGelisTarihi ? new Date(a.DosyaGelisTarihi ?? a.dosyaGelisTarihi) : new Date(0);
          bValue = b.DosyaGelisTarihi ?? b.dosyaGelisTarihi ? new Date(b.DosyaGelisTarihi ?? b.dosyaGelisTarihi) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'tahkimBasvuruTarihi':
          aValue = a.TahkimBasvuruTarihi ?? a.tahkimBasvuruTarihi ? new Date(a.TahkimBasvuruTarihi ?? a.tahkimBasvuruTarihi) : new Date(0);
          bValue = b.TahkimBasvuruTarihi ?? b.tahkimBasvuruTarihi ? new Date(b.TahkimBasvuruTarihi ?? b.tahkimBasvuruTarihi) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'raporTarihi':
          aValue = a.RaporTarihi ?? a.raporTarihi ? new Date(a.RaporTarihi ?? a.raporTarihi) : new Date(0);
          bValue = b.RaporTarihi ?? b.raporTarihi ? new Date(b.RaporTarihi ?? b.raporTarihi) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'zamanAsimiTarihi':
          aValue = a.ZamanAsimiTarihi ?? a.zamanAsimiTarihi ? new Date(a.ZamanAsimiTarihi ?? a.zamanAsimiTarihi) : new Date(0);
          bValue = b.ZamanAsimiTarihi ?? b.zamanAsimiTarihi ? new Date(b.ZamanAsimiTarihi ?? b.zamanAsimiTarihi) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'durusmaTarihi':
          aValue = a.DurusmaTarihi ?? a.durusmaTarihi ? new Date(a.DurusmaTarihi ?? a.durusmaTarihi) : new Date(0);
          bValue = b.DurusmaTarihi ?? b.durusmaTarihi ? new Date(b.DurusmaTarihi ?? b.durusmaTarihi) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'createdDate':
          aValue = a.CreatedDate ?? a.createdDate ? new Date(a.CreatedDate ?? a.createdDate) : new Date(0);
          bValue = b.CreatedDate ?? b.createdDate ? new Date(b.CreatedDate ?? b.createdDate) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        case 'updatedDate':
          aValue = a.UpdatedDate ?? a.updatedDate ? new Date(a.UpdatedDate ?? a.updatedDate) : new Date(0);
          bValue = b.UpdatedDate ?? b.updatedDate ? new Date(b.UpdatedDate ?? b.updatedDate) : new Date(0);
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        default:
          // Diğer kolonlar için string karşılaştırma
          aValue = getValue(a, sortColumn.charAt(0).toUpperCase() + sortColumn.slice(1)).toLocaleLowerCase('tr-TR');
          bValue = getValue(b, sortColumn.charAt(0).toUpperCase() + sortColumn.slice(1)).toLocaleLowerCase('tr-TR');
          return sortDirection === 'asc' ? aValue.localeCompare(bValue, 'tr') : bValue.localeCompare(aValue, 'tr');
      }
    });
    return sorted;
  }, [filteredFiles, sortColumn, sortDirection]);

  // Kaynak dosyadan seçilen evrakları hedefe kopyalar (sıra korunur; yeni yükleme üste ekler → ters sırada yükle)
  const copyDocumentsBetweenFiles = async (sourceFileId, targetFileId, documentIds = null) => {
    try {
      const srcDocs = await documentAPI.getByCaseFile(sourceFileId);
      if (!Array.isArray(srcDocs) || srcDocs.length === 0) return { copied: 0, total: 0 };

      let docsToCopy = srcDocs;
      if (Array.isArray(documentIds) && documentIds.length > 0) {
        const idSet = new Set(documentIds.map((id) => Number(id)));
        docsToCopy = srcDocs.filter((d) => idSet.has(Number(d.Id ?? d.id)));
      }
      if (docsToCopy.length === 0) return { copied: 0, total: 0 };

      let copied = 0;
      // Listedeki üstteki (SortOrder küçük) hedefte de üstte kalsın diye sondan başa yükle
      const orderedDocs = [...docsToCopy].reverse();
      for (const d of orderedDocs) {
        try {
          const docId = d.Id ?? d.id;
          const fileName = d.FileName ?? d.fileName ?? 'belge';
          const docType = d.DocumentType ?? d.documentType ?? 0;
          const desc = d.Description ?? d.description ?? '';
          const result = await documentAPI.download(docId);
          const blob = result?.blob ?? result;
          const mime = result?.contentType || blob.type || 'application/octet-stream';
          const fileObj = new File([blob], fileName, { type: mime });
          await documentAPI.upload(targetFileId, fileObj, desc, docType);
          copied++;
        } catch (docErr) {
          console.error('Evrak kopyalanamadı:', docErr);
        }
      }

      if (copied < docsToCopy.length) {
        alert(`${docsToCopy.length} evraktan ${copied} tanesi kopyalandı. Bazı evraklar kopyalanamadı, lütfen dosya detayından kontrol edin.`);
      }
      return { copied, total: docsToCopy.length };
    } catch (err) {
      console.error('Kaynak dosyanın evrakları alınamadı:', err);
      return { copied: 0, total: 0 };
    }
  };

  const handleCreate = async (caseFileData) => {
    setLoading(true);
    try {
      // Helper function to convert empty strings to null
      const toNullIfEmpty = (value) => {
        if (value === null || value === undefined) return null;
        if (typeof value === 'string' && value.trim() === '') return null;
        return value;
      };


      // Formun ic kullanimina ait anahtarlar (__ ile baslayanlar) API'ye
      // gitmez; gerisi oldugu gibi gonderilir.
      const gonderilecek = {};
      Object.keys(caseFileData).forEach((k) => {
        if (!k.startsWith('__')) gonderilecek[k] = caseFileData[k];
      });

      // Clean and format the data before sending
      const cleanedData = {
        // ONEMLI: Once formdaki tum alanlar aktarilir, sonra asagidaki acik
        // satirlar tur donusumu ve bos->null duzeltmesi icin uzerine yazar.
        // Burada eskiden yalnizca acik bir izin listesi vardi; forma sonradan
        // eklenen alanlar (muvekkil adresi, sirket yetkilisi, kimlik bilgileri)
        // listede olmadiklari icin yeni dosya olustururken sessizce
        // kayboluyordu. Guncelleme tarafi (handleUpdate) zaten boyle calisiyor.
        ...gonderilecek,
        // Required fields
        FoyNo: parseInt(caseFileData.FoyNo) || 0,
        Muvekkil: caseFileData.Muvekkil ? caseFileData.Muvekkil.trim() : '',
        MuvekkilTC: toNullIfEmpty(caseFileData.MuvekkilTC),
        IsSirket: !!caseFileData.IsSirket,
        MuvekkilAracSigortaSirketi: toNullIfEmpty(caseFileData.MuvekkilAracSigortaSirketi),
        Status: parseInt(caseFileData.Status) || 1,
        DavaTuru: parseInt(caseFileData.DavaTuru) || 0,
        // Convert empty strings to null for optional fields
        MuvekkilTelefon: toNullIfEmpty(caseFileData.MuvekkilTelefon),
        MuvekkilIBAN: toNullIfEmpty(caseFileData.MuvekkilIBAN),
        KarsiTarafSigortaSirketi: toNullIfEmpty(caseFileData.KarsiTarafSigortaSirketi),
        KarsiTaraf1: toNullIfEmpty(caseFileData.KarsiTaraf1),
        KarsiTaraf2: toNullIfEmpty(caseFileData.KarsiTaraf2),
        MuvekkilPlaka: toNullIfEmpty(caseFileData.MuvekkilPlaka),
        MuvekkilAracRolu: toNullIfEmpty(caseFileData.MuvekkilAracRolu),
        KarsiTarafPlaka: toNullIfEmpty(caseFileData.KarsiTarafPlaka),
        KazaSehri: toNullIfEmpty(caseFileData.KazaSehri),
        SulhDurumu: toNullIfEmpty(caseFileData.SulhDurumu),
        KarsiTarafSigortaSirketiVekili: toNullIfEmpty(caseFileData.KarsiTarafSigortaSirketiVekili),
        IcraDosyaNo: toNullIfEmpty(caseFileData.IcraDosyaNo),
        MahkemaBilgisi: toNullIfEmpty(caseFileData.MahkemaBilgisi),
        MahkemeEsas: toNullIfEmpty(caseFileData.MahkemeEsas),
        SavcilikCezaDosyaNo: toNullIfEmpty(caseFileData.SavcilikCezaDosyaNo),
        TahkimBasvuruNo: toNullIfEmpty(caseFileData.TahkimBasvuruNo),
        ItirazBasvuruNo: toNullIfEmpty(caseFileData.ItirazBasvuruNo),
        IbraAsamasi: toNullIfEmpty(caseFileData.IbraAsamasi),
        GelenOdemeDurumu: toNullIfEmpty(caseFileData.GelenOdemeDurumu),
        GidenOdemeDurumu: toNullIfEmpty(caseFileData.GidenOdemeDurumu),
        SMM: toNullIfEmpty(caseFileData.SMM),
        SozlesmeOrani: caseFileData.SozlesmeOrani ? (typeof caseFileData.SozlesmeOrani === 'number' ? caseFileData.SozlesmeOrani : parseFloat(caseFileData.SozlesmeOrani)) : null,
        SozlesmeOraniKdvDahil: !!(caseFileData.SozlesmeOraniKdvDahil ?? caseFileData.sozlesmeOraniKdvDahil),
        ReferansId: caseFileData.ReferansId ? (typeof caseFileData.ReferansId === 'number' ? caseFileData.ReferansId : parseInt(caseFileData.ReferansId)) : null,
        SorumluId: caseFileData.SorumluId ? (typeof caseFileData.SorumluId === 'number' ? caseFileData.SorumluId : parseInt(caseFileData.SorumluId)) : null,
        // Convert datetime-local strings to ISO format or null
        KazaTarihi: toISODateOrNull(caseFileData.KazaTarihi),
        DosyaGelisTarihi: toISODateOrNull(caseFileData.DosyaGelisTarihi),
        TahkimBasvuruTarihi: toISODateOrNull(caseFileData.TahkimBasvuruTarihi),
        RaporTarihi: toISODateOrNull(caseFileData.RaporTarihi),
        ZamanAsimiTarihi: toISODateOrNull(caseFileData.ZamanAsimiTarihi),
        DurusmaTarihi: toISODateOrNull(caseFileData.DurusmaTarihi),
        SigortaBasvuruTarihi: toISODateOrNull(caseFileData.SigortaBasvuruTarihi),
        DavaAcilisTarihi: toISODateOrNull(caseFileData.DavaAcilisTarihi),
        FizikiDosyaMevcut: !!(caseFileData.FizikiDosyaMevcut ?? caseFileData.fizikiDosyaMevcut),
        IsOzel: !!ozelMode,
        // İcra alanları
        TakipAcilisMiktari: caseFileData.TakipAcilisMiktari ? (typeof caseFileData.TakipAcilisMiktari === 'number' ? caseFileData.TakipAcilisMiktari : parseFloat(caseFileData.TakipAcilisMiktari)) : null,
        TakipTarihi: toISODateOrNull(caseFileData.TakipTarihi),
        IcraFaizTuru: toNullIfEmpty(caseFileData.IcraFaizTuru),
        IcraFaizOrani: caseFileData.IcraFaizOrani ? (typeof caseFileData.IcraFaizOrani === 'number' ? caseFileData.IcraFaizOrani : parseFloat(caseFileData.IcraFaizOrani)) : null,
        IcraFaizBaslangicTarihi: toISODateOrNull(caseFileData.IcraFaizBaslangicTarihi),
        IcraTahsilAsamasi: toNullIfEmpty(caseFileData.IcraTahsilAsamasi),
        IcraVekaletUcretiManuel: caseFileData.IcraVekaletUcretiManuel ? (typeof caseFileData.IcraVekaletUcretiManuel === 'number' ? caseFileData.IcraVekaletUcretiManuel : parseFloat(caseFileData.IcraVekaletUcretiManuel)) : null,
        IcraKonusu: toNullIfEmpty(caseFileData.IcraKonusu),
        KesinlesmeTarihi: toISODateOrNull(caseFileData.KesinlesmeTarihi),
        IcraHesapAyar: toNullIfEmpty(caseFileData.IcraHesapAyar),
        // Boşanma / Mal Rejimi
        MuvekkilDavaci: !!caseFileData.MuvekkilDavaci,
        MuvekkilDavali: !!caseFileData.MuvekkilDavali,
        EsAdSoyad: toNullIfEmpty(caseFileData.EsAdSoyad),
        EsTC: toNullIfEmpty(caseFileData.EsTC),
        EsCalisiyor: !!caseFileData.EsCalisiyor,
        EvlilikTarihi: toISODateOrNull(caseFileData.EvlilikTarihi),
        AyrilikTarihi: toISODateOrNull(caseFileData.AyrilikTarihi),
        BosanmaSebebi: toNullIfEmpty(caseFileData.BosanmaSebebi),
        CocukSayisi: caseFileData.CocukSayisi === '' || caseFileData.CocukSayisi == null ? null : parseInt(caseFileData.CocukSayisi),
        CocuklarJson: toNullIfEmpty(caseFileData.CocuklarJson),
        VelayetTalebi: !!caseFileData.VelayetTalebi,
        TalepNafaka: !!caseFileData.TalepNafaka,
        TalepTedbirNafaka: !!caseFileData.TalepTedbirNafaka,
        TalepIstirakNafaka: !!caseFileData.TalepIstirakNafaka,
        TalepYoksullukNafaka: !!caseFileData.TalepYoksullukNafaka,
        TalepMaddiTazminat: !!caseFileData.TalepMaddiTazminat,
        TalepManeviTazminat: !!caseFileData.TalepManeviTazminat,
        OdemeBilgileriGoster: !!caseFileData.OdemeBilgileriGoster,
        DetayAlanGoster: typeof caseFileData.DetayAlanGoster === 'string'
          ? (caseFileData.DetayAlanGoster || null)
          : (caseFileData.DetayAlanGoster && Object.keys(caseFileData.DetayAlanGoster).length
            ? JSON.stringify(caseFileData.DetayAlanGoster)
            : null),
        // Ceza / Soruşturma
        MuvekkilSanik: !!caseFileData.MuvekkilSanik,
        MuvekkilMusteki: !!caseFileData.MuvekkilMusteki,
        MuvekkilSupheli: !!caseFileData.MuvekkilSupheli,
        SucTuru: toNullIfEmpty(caseFileData.SucTuru),
        SucTarihi: toISODateOrNull(caseFileData.SucTarihi),
        SucYeri: toNullIfEmpty(caseFileData.SucYeri),
        TckMaddesi: toNullIfEmpty(caseFileData.TckMaddesi),
        TedbirTutuklu: !!caseFileData.TedbirTutuklu,
        TedbirAdliKontrol: !!caseFileData.TedbirAdliKontrol,
        TedbirYakalamaKarari: !!caseFileData.TedbirYakalamaKarari,
        KovusturmayaYerOlmadigiKarari: !!caseFileData.KovusturmayaYerOlmadigiKarari,
        IddianameTarihi: toISODateOrNull(caseFileData.IddianameTarihi),
        TarafSayisi: caseFileData.TarafSayisi === '' || caseFileData.TarafSayisi == null ? null : parseInt(caseFileData.TarafSayisi),
        TaraflarJson: toNullIfEmpty(caseFileData.TaraflarJson),
        IsverenFirma: toNullIfEmpty(caseFileData.IsverenFirma),
        IsverenTcvkn: toNullIfEmpty(caseFileData.IsverenTcvkn),
        SgkSicilNo: toNullIfEmpty(caseFileData.SgkSicilNo),
        IseGirisTarihi: toISODateOrNull(caseFileData.IseGirisTarihi),
        IstenCikisTarihi: toISODateOrNull(caseFileData.IstenCikisTarihi),
        CikisNedeni: toNullIfEmpty(caseFileData.CikisNedeni),
        SonUcret: caseFileData.SonUcret ? (typeof caseFileData.SonUcret === 'number' ? caseFileData.SonUcret : parseFloat(caseFileData.SonUcret)) : null,
        TalepKidem: !!caseFileData.TalepKidem,
        TalepIhbar: !!caseFileData.TalepIhbar,
        TalepFazlaMesai: !!caseFileData.TalepFazlaMesai,
        TalepUbgt: !!caseFileData.TalepUbgt,
        TalepHaftaTatili: !!caseFileData.TalepHaftaTatili,
        TalepYillikIzin: !!caseFileData.TalepYillikIzin,
        TalepAgi: !!caseFileData.TalepAgi,
        TalepMaasAlacagi: !!caseFileData.TalepMaasAlacagi,
        ArabuluculukTarihi: toISODateOrNull(caseFileData.ArabuluculukTarihi),
        SonTutanakTarihi: toISODateOrNull(caseFileData.SonTutanakTarihi),
        ArabuluculukSonucu: [1, 2, 3].includes(parseInt(caseFileData.ArabuluculukSonucu, 10)) ? parseInt(caseFileData.ArabuluculukSonucu, 10) : null,
        ArabuluculukDosyaNo: String(caseFileData.ArabuluculukDosyaNo || '').trim() || null,
        IscilikDavaTarihi: toISODateOrNull(caseFileData.IscilikDavaTarihi),
        BilirkisiTarihi: toISODateOrNull(caseFileData.BilirkisiTarihi),
        IslahTarihi: toISODateOrNull(caseFileData.IslahTarihi)
      };

      // Validate ReferansId before sending
      if (!cleanedData.ReferansId || cleanedData.ReferansId <= 0) {
        alert('Lütfen bir referans seçin!');
        return;
      }


      const createdFile = await caseFileAPI.create(cleanedData);
      const newFileId = createdFile && (createdFile.Id || createdFile.id);

      // Mevcut müvekkil dosyasından evrak kopyalama isteği geldiyse,
      // YÖNLENDİRMEDEN ÖNCE ve kaynak dosyadaki sırayı koruyarak (sıralı) kopyala.
      if (newFileId && caseFileData.__copyDocumentsFromFileId) {
        await copyDocumentsBetweenFiles(
          caseFileData.__copyDocumentsFromFileId,
          newFileId,
          caseFileData.__copyDocumentIds || null
        );
      }

      await loadCaseFiles();
      setShowForm(false);
      
      // Dosya detay sayfasına yönlendir
      if (newFileId) {
        // Belgeler zaten yukarıda kopyalandığı için kısa bir gecikme yeterli
        setTimeout(() => {
          navigate(ozelMode ? `/ozel-dosyalar/${newFileId}` : `/case-files/${newFileId}`);
        }, 500);
      } else {
        alert('Dosya başarıyla oluşturuldu');
      }
      
      return createdFile;
    } catch (error) {
      let errorMessage = 'Bilinmeyen bir hata oluştu';
      
      // Backend'den gelen hata mesajını al
      if (error.response?.data) {
        if (error.response.data.message) {
          errorMessage = error.response.data.message;
          
          // ModelState validation hatalarını göster
          if (error.response.data.errorDetails && Array.isArray(error.response.data.errorDetails)) {
            const validationErrors = error.response.data.errorDetails
              .map(detail => `${detail.field}: ${detail.errors.join(', ')}`)
              .join('\n');
            errorMessage += '\n\nValidation Hataları:\n' + validationErrors;
            console.error('Validation Errors:', error.response.data.errorDetails);
          } else if (error.response.data.errors) {
            // Eski format için
            if (typeof error.response.data.errors === 'object') {
              const errorObj = error.response.data.errors;
              const errorList = Object.keys(errorObj).map(key => `${key}: ${errorObj[key].join(', ')}`).join('\n');
              errorMessage += '\n\nValidation Hataları:\n' + errorList;
              console.error('Validation Errors:', errorObj);
            } else if (Array.isArray(error.response.data.errors)) {
              errorMessage += '\n\nValidation Hataları:\n' + error.response.data.errors.join('\n');
            }
          }
        } else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // 500 hatası için daha açıklayıcı mesaj
      if (error.response?.status === 500) {
        if (!errorMessage || errorMessage.includes('Bilinmeyen') || errorMessage.includes('Request failed')) {
          errorMessage = 'Sunucu hatası oluştu. Lütfen sistem yöneticisine başvurun.';
        }
      }
      
      alert('Hata: ' + errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (caseFileData) => {
    setLoading(true);
    try {
      // Clean and format the data before sending
      const cleanedData = {
        ...caseFileData,
        CaseFileId: caseFileData.CaseFileId || caseFileData.Id || caseFileData.id,
        ReferansId: caseFileData.ReferansId ? (typeof caseFileData.ReferansId === 'number' ? caseFileData.ReferansId : parseInt(caseFileData.ReferansId)) : null,
        // Convert empty strings to null for optional fields
        MuvekkilTC: caseFileData.MuvekkilTC || null,
        IsSirket: !!caseFileData.IsSirket,
        MuvekkilTelefon: caseFileData.MuvekkilTelefon || null,
        MuvekkilIBAN: caseFileData.MuvekkilIBAN || null,
        MuvekkilAracSigortaSirketi: caseFileData.MuvekkilAracSigortaSirketi || null,
        KarsiTarafSigortaSirketi: caseFileData.KarsiTarafSigortaSirketi || null,
        KarsiTaraf1: caseFileData.KarsiTaraf1 || null,
        KarsiTaraf2: caseFileData.KarsiTaraf2 || null,
        MuvekkilPlaka: caseFileData.MuvekkilPlaka || null,
        MuvekkilAracRolu: caseFileData.MuvekkilAracRolu || null,
        KarsiTarafPlaka: caseFileData.KarsiTarafPlaka || null,
        KazaSehri: caseFileData.KazaSehri || null,
        SulhDurumu: caseFileData.SulhDurumu || null,
        KarsiTarafSigortaSirketiVekili: caseFileData.KarsiTarafSigortaSirketiVekili || null,
        IcraDosyaNo: caseFileData.IcraDosyaNo || null,
        MahkemaBilgisi: caseFileData.MahkemaBilgisi || null,
        MahkemeEsas: caseFileData.MahkemeEsas || null,
        SavcilikCezaDosyaNo: caseFileData.SavcilikCezaDosyaNo || null,
        TahkimBasvuruNo: caseFileData.TahkimBasvuruNo || null,
        ItirazBasvuruNo: caseFileData.ItirazBasvuruNo || null,
        IbraAsamasi: caseFileData.IbraAsamasi || null,
        GelenOdemeDurumu: caseFileData.GelenOdemeDurumu || null,
        GidenOdemeDurumu: caseFileData.GidenOdemeDurumu || null,
        SMM: caseFileData.SMM || null,
        SozlesmeOrani: caseFileData.SozlesmeOrani ? (typeof caseFileData.SozlesmeOrani === 'number' ? caseFileData.SozlesmeOrani : parseFloat(caseFileData.SozlesmeOrani)) : null,
        SozlesmeOraniKdvDahil: !!(caseFileData.SozlesmeOraniKdvDahil ?? caseFileData.sozlesmeOraniKdvDahil),
        // Convert datetime-local strings to ISO format or null (UTC+3 Istanbul timezone)
        KazaTarihi: toISODateOrNull(caseFileData.KazaTarihi),
        DosyaGelisTarihi: toISODateOrNull(caseFileData.DosyaGelisTarihi),
        TahkimBasvuruTarihi: toISODateOrNull(caseFileData.TahkimBasvuruTarihi),
        RaporTarihi: toISODateOrNull(caseFileData.RaporTarihi),
        ZamanAsimiTarihi: toISODateOrNull(caseFileData.ZamanAsimiTarihi),
        DurusmaTarihi: toISODateOrNull(caseFileData.DurusmaTarihi),
        SigortaBasvuruTarihi: toISODateOrNull(caseFileData.SigortaBasvuruTarihi),
        DavaAcilisTarihi: toISODateOrNull(caseFileData.DavaAcilisTarihi),
        FizikiDosyaMevcut: !!(caseFileData.FizikiDosyaMevcut ?? caseFileData.fizikiDosyaMevcut),
        IsOzel: !!ozelMode,
        // İcra alanları
        TakipAcilisMiktari: caseFileData.TakipAcilisMiktari ? (typeof caseFileData.TakipAcilisMiktari === 'number' ? caseFileData.TakipAcilisMiktari : parseFloat(caseFileData.TakipAcilisMiktari)) : null,
        TakipTarihi: toISODateOrNull(caseFileData.TakipTarihi),
        IcraFaizTuru: (caseFileData.IcraFaizTuru && String(caseFileData.IcraFaizTuru).trim() !== '') ? caseFileData.IcraFaizTuru : null,
        IcraFaizOrani: caseFileData.IcraFaizOrani ? (typeof caseFileData.IcraFaizOrani === 'number' ? caseFileData.IcraFaizOrani : parseFloat(caseFileData.IcraFaizOrani)) : null,
        IcraFaizBaslangicTarihi: toISODateOrNull(caseFileData.IcraFaizBaslangicTarihi),
        IcraTahsilAsamasi: (caseFileData.IcraTahsilAsamasi && String(caseFileData.IcraTahsilAsamasi).trim() !== '') ? caseFileData.IcraTahsilAsamasi : null,
        IcraVekaletUcretiManuel: caseFileData.IcraVekaletUcretiManuel ? (typeof caseFileData.IcraVekaletUcretiManuel === 'number' ? caseFileData.IcraVekaletUcretiManuel : parseFloat(caseFileData.IcraVekaletUcretiManuel)) : null,
        IcraKonusu: (caseFileData.IcraKonusu && String(caseFileData.IcraKonusu).trim() !== '') ? caseFileData.IcraKonusu : null,
        KesinlesmeTarihi: toISODateOrNull(caseFileData.KesinlesmeTarihi),
        IcraHesapAyar: (caseFileData.IcraHesapAyar && String(caseFileData.IcraHesapAyar).trim() !== '') ? caseFileData.IcraHesapAyar : null,
        // Boşanma / Mal Rejimi
        MuvekkilDavaci: !!caseFileData.MuvekkilDavaci,
        MuvekkilDavali: !!caseFileData.MuvekkilDavali,
        EsAdSoyad: (caseFileData.EsAdSoyad && String(caseFileData.EsAdSoyad).trim() !== '') ? caseFileData.EsAdSoyad : null,
        EsTC: (caseFileData.EsTC && String(caseFileData.EsTC).trim() !== '') ? caseFileData.EsTC : null,
        EsCalisiyor: !!caseFileData.EsCalisiyor,
        EvlilikTarihi: toISODateOrNull(caseFileData.EvlilikTarihi),
        AyrilikTarihi: toISODateOrNull(caseFileData.AyrilikTarihi),
        BosanmaSebebi: (caseFileData.BosanmaSebebi && String(caseFileData.BosanmaSebebi).trim() !== '') ? caseFileData.BosanmaSebebi : null,
        CocukSayisi: caseFileData.CocukSayisi === '' || caseFileData.CocukSayisi == null ? null : parseInt(caseFileData.CocukSayisi),
        CocuklarJson: (caseFileData.CocuklarJson && String(caseFileData.CocuklarJson).trim() !== '') ? caseFileData.CocuklarJson : null,
        VelayetTalebi: !!caseFileData.VelayetTalebi,
        TalepNafaka: !!caseFileData.TalepNafaka,
        TalepTedbirNafaka: !!caseFileData.TalepTedbirNafaka,
        TalepIstirakNafaka: !!caseFileData.TalepIstirakNafaka,
        TalepYoksullukNafaka: !!caseFileData.TalepYoksullukNafaka,
        TalepMaddiTazminat: !!caseFileData.TalepMaddiTazminat,
        TalepManeviTazminat: !!caseFileData.TalepManeviTazminat,
        OdemeBilgileriGoster: !!caseFileData.OdemeBilgileriGoster,
        DetayAlanGoster: typeof caseFileData.DetayAlanGoster === 'string'
          ? (caseFileData.DetayAlanGoster || null)
          : (caseFileData.DetayAlanGoster && Object.keys(caseFileData.DetayAlanGoster).length
            ? JSON.stringify(caseFileData.DetayAlanGoster)
            : null),
        MuvekkilSanik: !!caseFileData.MuvekkilSanik,
        MuvekkilMusteki: !!caseFileData.MuvekkilMusteki,
        MuvekkilSupheli: !!caseFileData.MuvekkilSupheli,
        SucTuru: (caseFileData.SucTuru && String(caseFileData.SucTuru).trim() !== '') ? caseFileData.SucTuru : null,
        SucTarihi: toISODateOrNull(caseFileData.SucTarihi),
        SucYeri: (caseFileData.SucYeri && String(caseFileData.SucYeri).trim() !== '') ? caseFileData.SucYeri : null,
        TckMaddesi: (caseFileData.TckMaddesi && String(caseFileData.TckMaddesi).trim() !== '') ? caseFileData.TckMaddesi : null,
        TedbirTutuklu: !!caseFileData.TedbirTutuklu,
        TedbirAdliKontrol: !!caseFileData.TedbirAdliKontrol,
        TedbirYakalamaKarari: !!caseFileData.TedbirYakalamaKarari,
        KovusturmayaYerOlmadigiKarari: !!caseFileData.KovusturmayaYerOlmadigiKarari,
        IddianameTarihi: toISODateOrNull(caseFileData.IddianameTarihi),
        TarafSayisi: caseFileData.TarafSayisi === '' || caseFileData.TarafSayisi == null ? null : parseInt(caseFileData.TarafSayisi),
        TaraflarJson: (caseFileData.TaraflarJson && String(caseFileData.TaraflarJson).trim() !== '') ? caseFileData.TaraflarJson : null,
        IsverenFirma: (caseFileData.IsverenFirma && String(caseFileData.IsverenFirma).trim() !== '') ? caseFileData.IsverenFirma : null,
        IsverenTcvkn: (caseFileData.IsverenTcvkn && String(caseFileData.IsverenTcvkn).trim() !== '') ? caseFileData.IsverenTcvkn : null,
        SgkSicilNo: (caseFileData.SgkSicilNo && String(caseFileData.SgkSicilNo).trim() !== '') ? caseFileData.SgkSicilNo : null,
        IseGirisTarihi: toISODateOrNull(caseFileData.IseGirisTarihi),
        IstenCikisTarihi: toISODateOrNull(caseFileData.IstenCikisTarihi),
        CikisNedeni: (caseFileData.CikisNedeni && String(caseFileData.CikisNedeni).trim() !== '') ? caseFileData.CikisNedeni : null,
        SonUcret: caseFileData.SonUcret ? (typeof caseFileData.SonUcret === 'number' ? caseFileData.SonUcret : parseFloat(caseFileData.SonUcret)) : null,
        TalepKidem: !!caseFileData.TalepKidem,
        TalepIhbar: !!caseFileData.TalepIhbar,
        TalepFazlaMesai: !!caseFileData.TalepFazlaMesai,
        TalepUbgt: !!caseFileData.TalepUbgt,
        TalepHaftaTatili: !!caseFileData.TalepHaftaTatili,
        TalepYillikIzin: !!caseFileData.TalepYillikIzin,
        TalepAgi: !!caseFileData.TalepAgi,
        TalepMaasAlacagi: !!caseFileData.TalepMaasAlacagi,
        ArabuluculukTarihi: toISODateOrNull(caseFileData.ArabuluculukTarihi),
        SonTutanakTarihi: toISODateOrNull(caseFileData.SonTutanakTarihi),
        ArabuluculukSonucu: [1, 2, 3].includes(parseInt(caseFileData.ArabuluculukSonucu, 10)) ? parseInt(caseFileData.ArabuluculukSonucu, 10) : null,
        ArabuluculukDosyaNo: String(caseFileData.ArabuluculukDosyaNo || '').trim() || null,
        IscilikDavaTarihi: toISODateOrNull(caseFileData.IscilikDavaTarihi),
        BilirkisiTarihi: toISODateOrNull(caseFileData.BilirkisiTarihi),
        IslahTarihi: toISODateOrNull(caseFileData.IslahTarihi),
        // Ensure required fields are numbers
        FoyNo: parseInt(caseFileData.FoyNo) || 0,
        Status: parseInt(caseFileData.Status) || 1,
        DavaTuru: parseInt(caseFileData.DavaTuru) || 0
      };

      const updatedFile = await caseFileAPI.update(cleanedData);
      await loadCaseFiles();
      setShowForm(false);
      setEditingFile(null);
      alert('Dosya başarıyla güncellendi');
      return updatedFile;
    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.join(', ') || error.message || 'Bilinmeyen bir hata oluştu';
      alert('Hata: ' + errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (caseFileId) => {
    if (!await onayla('Bu dosyayı silmek istediğinizden emin misiniz?')) {
      return;
    }
    setLoading(true);
    try {
      await caseFileAPI.delete(caseFileId);
      await loadCaseFiles();
      alert('Dosya başarıyla silindi');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSendArchiveRequest = async (caseFileId) => {
    if (!await onayla('Bu dosya için arşiv isteği göndermek istediğinizden emin misiniz?')) {
      return;
    }
    setLoading(true);
    try {
      await archiveRequestAPI.sendRequest(caseFileId);
      alert('Arşiv isteği başarıyla gönderildi. Admin onayı bekleniyor.');
      await loadCaseFiles(); // Reload to update status
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      if (!filteredFiles || filteredFiles.length === 0) {
        alert('PDF oluşturmak için en az bir dosya olmalıdır.');
        return;
      }

      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(16);
      doc.text('Dosya Listesi', 14, 15);
      
      // Get visible columns (excluding actions column)
      const visibleCols = (columnOrder.length ? columnOrder : caseFileColumns.map(c => c.key))
        .map((k) => caseFileColumns.find((c) => c.key === k))
        .filter((col) => col && visibleColumns[col.key] === true);
      
      if (visibleCols.length === 0) {
        alert('PDF oluşturmak için en az bir sütun görünür olmalıdır.');
        return;
      }
      
      const headers = visibleCols.map(col => col.label);
      
      const tableData = filteredFiles.map(file => {
        const row = [];
        visibleCols.forEach(col => {
          let value = '';
          switch(col.key) {
            case 'foyNo':
              value = String(file.FoyNo ?? file.foyNo ?? '');
              break;
            case 'muvekkil':
              value = String(file.Muvekkil ?? file.muvekkil ?? '').substring(0, 30);
              break;
            case 'muvekkilTC':
              value = String(file.MuvekkilTC ?? file.muvekkilTC ?? '').substring(0, 11);
              break;
            case 'dosyaTuru':
              value = davaTuruEtiketi(file) ||  '';
              break;
            case 'muvekkilAracSigortaSirketi':
              value = String(file.MuvekkilAracSigortaSirketi ?? file.muvekkilAracSigortaSirketi ?? '').substring(0, 25);
              break;
            case 'sorumlu':
              value = String(file.SorumluAdi ?? file.sorumluAdi ?? '').substring(0, 25);
              break;
            case 'karsiSigorta':
              value = String(file.KarsiTarafSigortaSirketi ?? file.karsiTarafSigortaSirketi ?? file.KarsiSigorta ?? file.karsiSigorta ?? '').substring(0, 25);
              break;
            case 'karsiTaraf1':
              value = String(file.KarsiTaraf1 ?? file.karsiTaraf1 ?? '').substring(0, 25);
              break;
            case 'karsiTaraf2':
              value = String(file.KarsiTaraf2 ?? file.karsiTaraf2 ?? '').substring(0, 25);
              break;
            case 'muvekkilTelefon':
              value = String(file.MuvekkilTelefon ?? file.muvekkilTelefon ?? '');
              break;
            case 'muvekkilIBAN':
              value = String(file.MuvekkilIBAN ?? file.muvekkilIBAN ?? '');
              break;
            case 'sigortaBasvuruTarihi':
              value = file.SigortaBasvuruTarihi ? formatDate(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi) : '';
              break;
            case 'davaAcilisTarihi':
              value = file.DavaAcilisTarihi ? formatDate(file.DavaAcilisTarihi ?? file.davaAcilisTarihi) : '';
              break;
            case 'sozlesmeOrani':
              value = String(file.SozlesmeOrani ?? file.sozlesmeOrani ?? '');
              break;
            case 'mahkemeEsas':
              value = String(file.MahkemeEsas ?? file.mahkemeEsas ?? '');
              break;
            case 'muvekkilPlaka':
              value = String(file.MuvekkilPlaka ?? file.muvekkilPlaka ?? '');
              break;
            case 'karsiTarafPlaka':
              value = String(file.KarsiTarafPlaka ?? file.karsiTarafPlaka ?? '');
              break;
            case 'kazaSehri':
              value = String(file.KazaSehri ?? file.kazaSehri ?? '');
              break;
            case 'kazaTarihi':
              value = file.KazaTarihi ? formatDate(file.KazaTarihi ?? file.kazaTarihi) : '';
              break;
            case 'dosyaGelisTarihi':
              value = file.DosyaGelisTarihi ? formatDate(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi) : '';
              break;
            case 'davaTuru':
              value = davaTuruEtiketi(file) ||  '';
              break;
            case 'tahkimBasvuruTarihi':
              value = file.TahkimBasvuruTarihi ? formatDate(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi) : '';
              break;
            case 'referans':
              value = String(file.ReferansAdi ?? file.referansAdi ?? file.Referans ?? file.referans ?? '').substring(0, 20);
              break;
            case 'raporTarihi':
              value = file.RaporTarihi ? formatDate(file.RaporTarihi ?? file.raporTarihi) : '';
              break;
            case 'zamanAsimiTarihi':
              value = file.ZamanAsimiTarihi ? formatDate(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi) : '';
              break;
            case 'durusmaTarihi':
              value = file.DurusmaTarihi ? formatDateForDisplay(file.DurusmaTarihi ?? file.durusmaTarihi) : '';
              break;
            case 'status':
              value = CASE_FILE_STATUS_LABELS[file.Status ?? file.status] ?? '';
              break;
            case 'karsiTarafVekili':
              value = String(file.KarsiTarafSigortaSirketiVekili ?? file.karsiTarafSigortaSirketiVekili ?? '').substring(0, 25);
              break;
            case 'icraDosyaNo':
              value = String(file.IcraDosyaNo ?? file.icraDosyaNo ?? '');
              break;
            case 'savcilikCezaDosyaNo':
              value = String(file.SavcilikCezaDosyaNo ?? file.savcilikCezaDosyaNo ?? '');
              break;
            case 'tahkimBasvuruNo':
              value = String(file.TahkimBasvuruNo ?? file.tahkimBasvuruNo ?? '');
              break;
            case 'itirazBasvuruNo':
              value = String(file.ItirazBasvuruNo ?? file.itirazBasvuruNo ?? '');
              break;
            case 'mahkemaBilgisi':
              value = String(file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? '').substring(0, 30);
              break;
            case 'sulhDurumu':
              value = String(file.SulhDurumu ?? file.sulhDurumu ?? '');
              break;
            case 'ibraAsamasi':
              value = String(file.IbraAsamasi ?? file.ibraAsamasi ?? '');
              break;
            case 'gelenOdeme':
              value = String(file.GelenOdemeDurumu ?? file.gelenOdemeDurumu ?? '');
              break;
            case 'gidenOdeme':
              value = String(file.GidenOdemeDurumu ?? file.gidenOdemeDurumu ?? '');
              break;
            case 'smm':
              value = String(file.SMM ?? file.smm ?? '');
              break;
            case 'sonIslemYapan':
              value = String(file.SonIslemYapanAdi ?? file.sonIslemYapanAdi ?? '').substring(0, 25);
              break;
            case 'createdDate':
              value = file.CreatedDate ? formatDate(file.CreatedDate ?? file.createdDate) : '';
              break;
            case 'updatedDate':
              value = file.UpdatedDate ? formatDate(file.UpdatedDate ?? file.updatedDate) : '';
              break;
            default:
              value = '';
          }
          row.push(value);
        });
        return row;
      });

      if (tableData.length === 0) {
        alert('PDF oluşturmak için veri bulunamadı.');
        return;
      }

      // Check if autoTable is available
      if (typeof autoTable !== 'function') {
        alert('PDF tablo kütüphanesi yüklenemedi. Lütfen sayfayı yenileyin.');
        return;
      }

      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 25,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [66, 139, 202], textColor: 255 },
        margin: { top: 25 },
        theme: 'grid'
      });

      doc.save('dosya-listesi.pdf');
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu: ' + error.message);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      // Get visible columns
      const visibleCols = (columnOrder.length ? columnOrder : caseFileColumns.map(c => c.key))
        .map((k) => caseFileColumns.find((c) => c.key === k))
        .filter((col) => col && visibleColumns[col.key] === true);
      const headers = visibleCols.map(col => col.label);
      const worksheetData = [headers];

      filteredFiles.forEach(file => {
        const row = [];
        visibleCols.forEach(col => {
          let value = '';
          switch(col.key) {
            case 'foyNo':
              value = file.FoyNo ?? file.foyNo ?? '';
              break;
            case 'muvekkil':
              value = file.Muvekkil ?? file.muvekkil ?? '';
              break;
            case 'muvekkilTC':
              value = file.MuvekkilTC ?? file.muvekkilTC ?? '';
              break;
            case 'dosyaTuru':
              value = davaTuruEtiketi(file) ||  '';
              break;
            case 'muvekkilAracSigortaSirketi':
              value = file.MuvekkilAracSigortaSirketi ?? file.muvekkilAracSigortaSirketi ?? '';
              break;
            case 'sorumlu':
              value = file.SorumluAdi ?? file.sorumluAdi ?? '';
              break;
            case 'karsiSigorta':
              value = file.KarsiTarafSigortaSirketi ?? file.karsiTarafSigortaSirketi ?? file.KarsiSigorta ?? file.karsiSigorta ?? '';
              break;
            case 'karsiTaraf1':
              value = file.KarsiTaraf1 ?? file.karsiTaraf1 ?? '';
              break;
            case 'karsiTaraf2':
              value = file.KarsiTaraf2 ?? file.karsiTaraf2 ?? '';
              break;
            case 'muvekkilTelefon':
              value = file.MuvekkilTelefon ?? file.muvekkilTelefon ?? '';
              break;
            case 'muvekkilIBAN':
              value = file.MuvekkilIBAN ?? file.muvekkilIBAN ?? '';
              break;
            case 'sigortaBasvuruTarihi':
              value = file.SigortaBasvuruTarihi ? formatDate(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi) : '';
              break;
            case 'davaAcilisTarihi':
              value = file.DavaAcilisTarihi ? formatDate(file.DavaAcilisTarihi ?? file.davaAcilisTarihi) : '';
              break;
            case 'sozlesmeOrani':
              value = file.SozlesmeOrani ?? file.sozlesmeOrani ?? '';
              break;
            case 'mahkemeEsas':
              value = file.MahkemeEsas ?? file.mahkemeEsas ?? '';
              break;
            case 'muvekkilPlaka':
              value = file.MuvekkilPlaka ?? file.muvekkilPlaka ?? '';
              break;
            case 'karsiTarafPlaka':
              value = file.KarsiTarafPlaka ?? file.karsiTarafPlaka ?? '';
              break;
            case 'kazaSehri':
              value = file.KazaSehri ?? file.kazaSehri ?? '';
              break;
            case 'kazaTarihi':
              value = file.KazaTarihi ? formatDate(file.KazaTarihi ?? file.kazaTarihi) : '';
              break;
            case 'dosyaGelisTarihi':
              value = file.DosyaGelisTarihi ? formatDate(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi) : '';
              break;
            case 'davaTuru':
              value = davaTuruEtiketi(file) ||  '';
              break;
            case 'tahkimBasvuruTarihi':
              value = file.TahkimBasvuruTarihi ? formatDate(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi) : '';
              break;
            case 'referans':
              value = file.ReferansAdi ?? file.referansAdi ?? file.Referans ?? file.referans ?? '';
              break;
            case 'raporTarihi':
              value = file.RaporTarihi ? formatDate(file.RaporTarihi ?? file.raporTarihi) : '';
              break;
            case 'zamanAsimiTarihi':
              value = file.ZamanAsimiTarihi ? formatDate(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi) : '';
              break;
            case 'durusmaTarihi':
              value = file.DurusmaTarihi ? formatDateForDisplay(file.DurusmaTarihi ?? file.durusmaTarihi) : '';
              break;
            case 'status':
              value = CASE_FILE_STATUS_LABELS[file.Status ?? file.status] ?? '';
              break;
            case 'karsiTarafVekili':
              value = file.KarsiTarafSigortaSirketiVekili ?? file.karsiTarafSigortaSirketiVekili ?? '';
              break;
            case 'icraDosyaNo':
              value = file.IcraDosyaNo ?? file.icraDosyaNo ?? '';
              break;
            case 'savcilikCezaDosyaNo':
              value = file.SavcilikCezaDosyaNo ?? file.savcilikCezaDosyaNo ?? '';
              break;
            case 'tahkimBasvuruNo':
              value = file.TahkimBasvuruNo ?? file.tahkimBasvuruNo ?? '';
              break;
            case 'itirazBasvuruNo':
              value = file.ItirazBasvuruNo ?? file.itirazBasvuruNo ?? '';
              break;
            case 'mahkemaBilgisi':
              value = file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? '';
              break;
            case 'sulhDurumu':
              value = file.SulhDurumu ?? file.sulhDurumu ?? '';
              break;
            case 'ibraAsamasi':
              value = file.IbraAsamasi ?? file.ibraAsamasi ?? '';
              break;
            case 'gelenOdeme':
              value = file.GelenOdemeDurumu ?? file.gelenOdemeDurumu ?? '';
              break;
            case 'gidenOdeme':
              value = file.GidenOdemeDurumu ?? file.gidenOdemeDurumu ?? '';
              break;
            case 'smm':
              value = file.SMM ?? file.smm ?? '';
              break;
            case 'sonIslemYapan':
              value = file.SonIslemYapanAdi ?? file.sonIslemYapanAdi ?? '';
              break;
            case 'createdDate':
              value = file.CreatedDate ? formatDate(file.CreatedDate ?? file.createdDate) : '';
              break;
            case 'updatedDate':
              value = file.UpdatedDate ? formatDate(file.UpdatedDate ?? file.updatedDate) : '';
              break;
            default:
              value = '';
          }
          row.push(value);
        });
        worksheetData.push(row);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dosyalar');
      XLSX.writeFile(workbook, 'dosya-listesi.xlsx');
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Excel oluşturulurken hata oluştu');
    }
  };

  // Print
  const handlePrint = () => {
    window.print();
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedFilteredFiles.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedFilteredFiles.length / itemsPerPage);

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('tr-TR');
    } catch (e) {
      return date;
    }
  };

  return (
    <div className="case-files-page">
      <div className="page-header">
        <h1>{ozelMode ? 'Özel Dosyalar' : 'Dosyalar'}</h1>
        <div className="page-header-actions">
          <button
            className="btn btn-primary btn-upload btn-sm"
            onClick={() => {
              setEditingFile(null);
              setShowForm(true);
            }}
          >
            + Yeni Dosya
          </button>
        </div>
      </div>

      {/* Sayfa içi sekmeler */}
      <div className="case-file-tabs">
        <button
          className={`case-file-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          📁 {ozelMode ? 'Tüm Özel Dosyalar' : 'Tüm Dosyalar'}
        </button>
        {openTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            draggable
            onDragStart={(e) => handleTabDragStart(e, tab.id)}
            onDragOver={handleTabDragOver}
            onDrop={(e) => handleTabDrop(e, tab.id)}
            onDragEnd={handleTabDragEnd}
            className={`case-file-tab ${activeTab === tab.id ? 'active' : ''} ${draggingTabId === tab.id ? 'dragging' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={`${tab.foyNo ? tab.foyNo + ' - ' : ''}${tab.muvekkil}${sekmeTuru(tab) ? ' · ' + sekmeTuru(tab) : ''} — sürükleyerek sırayı değiştirin`}
          >
            <span className="case-file-tab-label">
              {tab.foyNo ? `#${tab.foyNo} ` : ''}{tab.muvekkil}
              {sekmeTuru(tab) ? (
                <span className="case-file-tab-dava"> · {sekmeTuru(tab)}</span>
              ) : null}
            </span>
            <span
              className="case-file-tab-close"
              onClick={(e) => closeFileTab(tab.id, e)}
              role="button"
              aria-label="Sekmeyi kapat"
            >
              ×
            </span>
          </button>
        ))}
      </div>

      {activeTab !== 'all' && (
        <CaseFileDetail key={activeTab} caseFileId={activeTab} embedded ozelMode={ozelMode} />
      )}

      <div style={{ display: activeTab === 'all' ? 'block' : 'none' }}>
      {/* Filter Panel */}
      <div className="filter-panel">
        <div className="filter-panel-left">
          <input
            type="text"
            placeholder="Ara (Foy No, Müvekkil)..."
            className="form-control"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <select
            className="form-control"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">Tüm Durumlar</option>
            {Object.entries(CASE_FILE_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            className="form-control"
            value={filters.davaTuru}
            onChange={(e) => handleFilterChange('davaTuru', e.target.value)}
          >
            <option value="">Tüm Dava Türleri</option>
            {getSortedDavaTuruEntries().map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="filter-panel-right">
          <ColumnControl
            columns={orderedAllColumns}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
          />
          <select 
            className="form-control"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            style={{ minWidth: '80px' }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Yükleniyor...</div>
      ) : (
        <>
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="case-files-table" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {orderedVisibleColumns.map((col) => (
                    <th
                      key={col.key}
                      draggable
                      onDragStart={(e) => handleColumnDragStart(e, col.key)}
                      onDragOver={handleColumnDragOver}
                      onDragEnter={(e) => handleColumnDragEnter(e, col.key)}
                      onDrop={(e) => handleColumnDrop(e, col.key)}
                      onDragEnd={handleColumnDragEnd}
                      className={`${draggingColumnKey === col.key ? 'col-dragging' : 'col-draggable'}${col.key === 'davaTuru' ? ' col-dava-turu' : ''}${col.key === 'muvekkil' ? ' col-muvekkil' : ''}${col.key === 'foyNo' ? ' col-foy-no' : ''}`}
                      style={{ cursor: 'grab', userSelect: 'none', minWidth: col.key === 'status' ? '150px' : undefined, width: col.key === 'status' ? '150px' : undefined }}
                      onClick={() => handleColumnHeaderClick(col.key)}
                      title="Sürükleyerek kolon sırasını değiştirin; tıklayarak sıralayın"
                    >
                      <span className="col-drag-handle" aria-hidden="true">⋮⋮</span>
                      {col.label} <SortIndicator column={col.key} />
                    </th>
                  ))}
                </tr>
                <tr className="column-filter-row">
                  {orderedVisibleColumns.map((col) => (
                    <th key={`filter-${col.key}`}>
                      <input
                        type="text"
                        className="column-filter-input"
                        placeholder="Ara..."
                        value={columnFilters[col.key] || ''}
                        onChange={(e) => setColumnFilters({ ...columnFilters, [col.key]: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={Math.max(orderedVisibleColumns.length, 1)} className="empty-table">
                      Henüz dosya bulunmamaktadır.
                    </td>
                  </tr>
                ) : (
                  currentItems.map((file) => {
                    const status = file.Status ?? file.status;
                    const davaTuru = file.DavaTuru ?? file.davaTuru;
                    const fileId = file.Id ?? file.id;
                    return (
                      <tr 
                        key={fileId}
                        className="case-file-row"
                        onClick={(e) => {
                          // Eğer bir butona tıklandıysa navigate etme
                          if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
                            return;
                          }
                          // Token kontrolü
                          const token = localStorage.getItem('token');
                          if (!token) {
                            alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
                            navigate('/login');
                            return;
                          }
                          openFileTab(file);
                        }}
                      >
                        {orderedVisibleColumns.map((col) => renderCaseFileCell(file, col.key, status, davaTuru))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-info">
            <p>
              {filteredFiles.length} kayıttan {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredFiles.length)} arasındaki kayıtlar gösteriliyor
              {caseFiles.length !== filteredFiles.length && ` (Toplam ${caseFiles.length} kayıttan filtrelendi)`}
            </p>
          </div>

          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && <span className="pagination-ellipsis">...</span>}
            {totalPages > 5 && (
              <button
                className={`pagination-btn ${currentPage === totalPages ? 'active' : ''}`}
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            )}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </>
      )}
      </div>

      {showForm && (
        <CaseFileForm
          caseFile={editingFile}
          ozelMode={ozelMode}
          onSubmit={editingFile ? handleUpdate : handleCreate}
          onCancel={() => {
            setShowForm(false);
            setEditingFile(null);
          }}
          loading={loading}
        />
      )}
    </div>
  );
};

export default CaseFiles;